import { useStyles } from '@webapps/styles';
import { css, cx } from '@emotion/css';
import { Button, MenuItem, Typography, } from '@material-ui/core';
import { CloseRounded, FormatItalicRounded, FormatBoldRounded, FormatUnderlinedRounded, FormatAlignLeftRounded, FormatAlignCenterRounded, FormatAlignRightRounded, DeleteRounded, AddRounded, CheckRounded } from '@material-ui/icons';
import { getDraftNumber, getUsedTextColors, saveContentOnStore } from '../../utils/services';
import { useEditor } from 'context/editor/useEditor';
import { ADD_DOCUMENT, DELETE_DOCUMENT, EDIT_DOCUMENT_ID, EDIT_DOCUMENT_NAME } from 'context/editor/editorActions';
import { useState, useEffect, useRef } from 'react';
import { deleteConventionModel, updateConventionModel } from '@webapps/business';
import { useAuth, useMutation, useOnClickOutside, useToast } from '@webapps/reactjs-tech-components';
import balisesStyles from '../../constants/balisesStyles';
import StyledSelect from '../StyledSelect';
import ColorPickerModal from '../ColorPickerModal';
import ModalWithImage from 'components/ModalWithImage';

const ConventionHeader = ({ setSelectedDocument, selectedDocument, curentIndex, editorRef, currentPageIndex }) => {
  const deleteModelMessage = `Le document "${selectedDocument?.name}" a été supprimé avec succès.`;

  const { classes } = useStyles(styles);
  const { state, dispatch } = useEditor();
  const { idUser } = useAuth();
  const { addToast } = useToast();

  // Local state pour l'input (pour éviter le lag lors de la saisie)
  const [title, setTitle] = useState(selectedDocument?.name ?? '');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const colorPickerRef = useRef(null);
  const [usedColors, setUsedColors] = useState([]);
  const [selectionInfoStyle, setSelectionInfoStyle] = useState({
    bold: false,
    italic: false,
    underline: false,
    align: 'left',
    color: '#000000',
    selectedStyle: 'normal',
  });
  const [isOpenColorPicker, setIsOpenColorPicker] = useState(false);

  useOnClickOutside(colorPickerRef, () => setIsOpenColorPicker(false));

  useEffect(() => {
    const usedColors = getUsedTextColors(selectedDocument);

    setUsedColors(usedColors);
  }, [selectedDocument]);

  useEffect(() => {
    const handler = () => {
      const styleKey = getCurrentStyleKeySoft(balisesStyles);

      setSelectedStyle(styleKey);
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  // Remet à jour l'input si on change de document sélectionné
  useEffect(() => {
    setTitle(selectedDocument?.name ?? '');
  }, [selectedDocument?.id, selectedDocument?.name]);

  const {
    isSuccess: isSuccessDeleteModel,
    mutateAsync: mutateDeleteModel,
  } = useMutation(deleteConventionModel);

  const {
    mutateAsync: mutateSaveModel,
  } = useMutation(updateConventionModel);

  const handleAddDocument = () => {
    const brouillonNumbers = getDraftNumber(state);
    const draft = {
      id: `brouillon ${brouillonNumbers + 1}`,
      name: `Brouillon ${brouillonNumbers + 1}`,
      pages: [
        {
          id: `page1`,
          text: '',
        }
      ],
    };

    dispatch({
      type: ADD_DOCUMENT,
      payload: draft,
    });

    setSelectedDocument(draft);

    addToast({
      status: 'success',
      message: 'Document ajouté avec succès',
      autoDismiss: true,
    });
  };

  function getSmartfieldPresence(htmlContent) {
    if (!htmlContent) return {};
  
    const smartfieldRegex = /<span\s+[^>]*data-smartfield=(?:"|\\")\[([a-zA-Z0-9_.-]+)\/\](?:"|\\")[^>]*>/gi;
    const smartfields = new Set();

    // Extract all unique smartfield keys
    let match;
    while ((match = smartfieldRegex.exec(htmlContent)) !== null) {
      const key = match[1]; // Extract the key (e.g., cfaName, businessOwner)
      smartfields.add(key);
    }
  
    // Convert Set to an object with keys set to true
    const presenceObject = {};
    smartfields.forEach(key => {
      presenceObject[key] = true;
    });
  
    return presenceObject;
  }

  const handleSaveModel = async () => {

    try {
      const documentFromStore = state.documents.find(d => d.id === selectedDocument.id);

      const htmlContent = JSON.stringify(documentFromStore.pages);

      const smartfieldPresence = getSmartfieldPresence(htmlContent);

      console.log(smartfieldPresence)

      let { data } = await mutateSaveModel({
        id: idUser,
        data: {
          ...smartfieldPresence,
          model: {
            id: documentFromStore?.id ? documentFromStore?.id : null,
            name: documentFromStore?.name,
            content: htmlContent // Sérialisation de `selectedDocument.pages` en chaîne JSON
          }
        }
      });

      // Modifier l'id pour mettre l'id de l'element en bdd et plus l'id brouillon
      dispatch({ type: EDIT_DOCUMENT_ID, payload: { documentId: selectedDocument.id, newId: data?.id } });

      setSelectedDocument({ ...selectedDocument, id: data?.id });

      addToast({
        status: 'success',
        message: `Le document "${selectedDocument?.name}" a été sauvegardé avec succès.`
      });
    } catch (error) {
      console.error('Error saving document:', error);
      addToast({
        status: 'error',
        message: 'Une erreur est survenue lors de la sauvegarde du document.'
      });
    }
  }

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    dispatch({
      type: EDIT_DOCUMENT_NAME,
      payload: {
        documentId: selectedDocument.id,
        name: e.target.value,
      },
    });
  };

  const onDeleteSuccess = (documentId) => {
    dispatch({
      type: DELETE_DOCUMENT,
      payload: { documentId },
    });


    setSelectedDocument(state.documents[0]);

    addToast({
      status: 'success',
      message: deleteModelMessage
    });

    handleCloseDeleteModal();
  }

  const handleDeleteContract = async (documentId) => {

    try {
      if (documentId && documentId.includes("brouillon")) {
        onDeleteSuccess(documentId)
      } else {
        await mutateDeleteModel({ id: documentId });
      }
    } catch (error) {
      console.error("Error deleting contract:", error);
    }
  };

  function getCurrentStyleKeySoft(balisesStyles) {
    const selection = window.getSelection();

    // Réinitialisation si pas de sélection valide ou si pas dans l'éditeur
    if (selection.type === 'Caret' || !selection.rangeCount || !isSelectionInEditor()) {
      setSelectionInfoStyle({
        bold: false,
        italic: false,
        underline: false,
        align: 'left',
        color: '#000000',
        selectedStyle: 'normal',
      });
      return '';
    }

    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // Remonter au premier élément HTML si on est sur un TextNode
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    const currentStyle = {
      bold: false,
      italic: false,
      underline: false,
      align: 'left',
      color: '#000000',
      selectedStyle: 'normal'
    };

    // Parcourir tous les parents jusqu'au body ou jusqu'à l'éditeur
    while (node && node !== document.body && node !== editorRef?.current) {
      const computedStyle = window.getComputedStyle(node);

      // Détection des styles de texte
      currentStyle.bold = currentStyle.bold || computedStyle.fontWeight === '700';
      currentStyle.italic = currentStyle.italic || computedStyle.fontStyle === 'italic';
      currentStyle.underline = currentStyle.underline || computedStyle.textDecoration.includes('underline');

      // Détection de la couleur
      if (computedStyle.color) {
        const hexColor = rgbToHex(computedStyle.color);
        if (hexColor && hexColor !== '#000000') {
          currentStyle.color = hexColor;
        }
      }

      // Détection spécifique des balises <font>
      if (node.tagName && node.tagName.toLowerCase() === 'font') {
        const fontColor = node.getAttribute('color');
        if (fontColor) {
          currentStyle.color = colorNameToHex(fontColor);
        }
      }

      // Détection des styles de titre
      if (!currentStyle.selectedStyle || currentStyle.selectedStyle === 'normal') {
        for (const [key, style] of Object.entries(balisesStyles)) {
          if (computedStyle.fontSize === style.fontSize &&
            computedStyle.fontFamily.replace(/"/g, '') === style.fontFamily) {
            currentStyle.selectedStyle = key;
            break;
          }
        }
      }

      node = node.parentElement;
    }

    setSelectionInfoStyle(currentStyle);
    return currentStyle.selectedStyle;
  }

  // Helper pour convertir RGB/RGBA en HEX
  function rgbToHex(rgb) {
    const match = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*\d+\.?\d*)?\)$/);
    if (match) {
      const r = parseInt(match[1], 10).toString(16).padStart(2, '0');
      const g = parseInt(match[2], 10).toString(16).padStart(2, '0');
      const b = parseInt(match[3], 10).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
    return null;
  }

  // Helper pour convertir les noms de couleur en HEX
  function colorNameToHex(color) {
    const colors = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#ff0000',
      'green': '#00ff00',
      'blue': '#0000ff',
      'yellow': '#ffff00',
      'cyan': '#00ffff',
      'magenta': '#ff00ff'
    };
    return colors[color.toLowerCase()] || color;
  }

  // Fonction pour vérifier si la sélection est dans l'éditeur
  const isSelectionInEditor = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount || !editorRef?.current) return false;

    const range = selection.getRangeAt(0);
    const selectionContainer = range.commonAncestorContainer;

    // Vérifier si la sélection est dans l'éditeur ou un de ses descendants
    return editorRef.current.contains(selectionContainer) ||
      editorRef.current.contains(selectionContainer.parentElement);
  };

  function applyCleanStyle(styleObj) {
    if (!isSelectionInEditor()) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);

    // Crée un nouveau span avec le style voulu
    const newSpan = document.createElement('span');
    Object.entries(styleObj).forEach(([key, value]) => {
      newSpan.style[key] = value;
    });

    // Clone le contenu sélectionné
    const content = range.extractContents();

    // Supprime tous les font-size et line-height sur les descendants
    content.querySelectorAll('*').forEach(node => {
      node.style.removeProperty('font-size');
      node.style.removeProperty('line-height');
    });

    newSpan.appendChild(content);
    range.insertNode(newSpan);

    // Replace la sélection
    selection.removeAllRanges();
    range.selectNodeContents(newSpan);
    selection.addRange(range);

    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  }

  const handleResetStyle = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('removeFormat');
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  }

  const handleBold = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('bold');
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  };

  const handleItalic = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('italic');
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  };

  const handleUnderline = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('underline');
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  };

  const handleAlignLeft = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('justifyLeft')
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  };

  const handleAlignCenter = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('justifyCenter')
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  }

  const handleAlignRight = () => {
    if (!isSelectionInEditor()) return;
    document.execCommand('justifyRight')
    saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
  }

  const handleOpenDeleteModal = () => {
    setIsOpenDeleteModal(true);
  }

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  }

  useEffect(() => {

    if (isSuccessDeleteModel) {
      onDeleteSuccess(selectedDocument?.id)
    }
  }, [isSuccessDeleteModel])

  return (
    <>
      <div className={classes["framerDiv"]}>
        <input
          className={classes["framerP"]}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            width: "300px",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: "15px",
            padding: 0,
            margin: 0,
            // Pour garder le style du texte
          }}
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Titre de la convention"
        />
        <div className={classes["framerDiv4"]}>
          <Button className={classes["actionButton"]}
            onClick={handleSaveModel}
          >
            <div className={classes["actionIcon"]}><CheckRounded /></div>
          </Button>
          <Button className={classes["actionButton"]}
            onClick={handleAddDocument}
          >
            <div className={classes["actionIcon"]}><AddRounded /></div>
          </Button>
          <Button className={cx(classes["actionButton"], css({
            cursor: selectedDocument?.id === 'default' ? 'not-allowed' : 'pointer'
          }))}
            onClick={() => handleOpenDeleteModal()}
            disabled={selectedDocument?.id === 'default'}
          >
            <div className={classes["actionIcon"]}><DeleteRounded /></div>
          </Button>
        </div>
      </div>
      <div className={classes["framerDiv2"]}>
        <div className={classes["framerDiv3"]}>
          <StyledSelect
            name="age"
            className={{
              padding: "10px 20px 10px 44px !important", // car la fleche fais 24 donc 20 + 24 = 44
              color: "red !important"
            }}
            value={selectionInfoStyle.selectedStyle}
            onChange={e => {
              applyCleanStyle(balisesStyles[e.target.value]);
            }}
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="title">Titre</MenuItem>
            <MenuItem value="subtitle">Sous-titre</MenuItem>
            <MenuItem value="title1">Titre 1</MenuItem>
            <MenuItem value="title2">Titre 2</MenuItem>
            <MenuItem value="title3">Titre 3</MenuItem>
            <MenuItem value="title4">Titre 4</MenuItem>
            <MenuItem value="title5">Titre 5</MenuItem>
          </StyledSelect>

          <Button className={classes["framerButton2"]} onClick={handleResetStyle}>
            <CloseRounded className={classes["framerIcon96"]} />
          </Button>
          <Button style={{ backgroundColor: selectionInfoStyle.bold ? "#cfdfff" : "#f3f7ff" }} className={classes["framerButton2"]} onClick={handleBold}>
            <FormatBoldRounded className={classes["framerIcon96"]} />
          </Button>
          <Button style={{ backgroundColor: selectionInfoStyle.italic ? "#cfdfff" : "#f3f7ff" }} className={classes["framerButton2"]} onClick={handleItalic}>
            <FormatItalicRounded className={classes["framerIcon96"]} />
          </Button>
          <Button style={{ backgroundColor: selectionInfoStyle.underline ? "#cfdfff" : "#f3f7ff" }} className={classes["framerButton2"]} onClick={handleUnderline}>
            <FormatUnderlinedRounded className={classes["framerIcon96"]} />
          </Button>
          <Button className={classes["framerButton2"]} onClick={handleAlignLeft}>
            <FormatAlignLeftRounded className={classes["framerIcon96"]} />
          </Button>
          <Button className={classes["framerButton2"]} onClick={handleAlignCenter}>
            <FormatAlignCenterRounded className={classes["framerIcon96"]} />
          </Button>
          <Button className={classes["framerButton2"]} onClick={handleAlignRight}>
            <FormatAlignRightRounded className={classes["framerIcon96"]} />
          </Button>
          <div
            style={{

            }}
          >
            <Button className={cx(classes["framerButton2"], classes["colorButton"], css({
              '.MuiButton-label': {
                backgroundColor: selectionInfoStyle.color,
                height: "16px",
                width: "16px",
              }
            }))}
              onClick={() => setIsOpenColorPicker(!isOpenColorPicker)}>
            </Button>
            {isOpenColorPicker && <div ref={colorPickerRef}>
              <ColorPickerModal
                onColorChange={(color) => {
                  if (!isSelectionInEditor()) return;
                  document.execCommand('foreColor', false, color);
                  setSelectionInfoStyle(prev => ({ ...prev, color }));
                  saveContentOnStore(selectedDocument, currentPageIndex, editorRef, dispatch);
                }}
                predefinedColors={usedColors}
              />
            </div>}
          </div>
        </div>
        {
          isOpenDeleteModal && (
            <ModalWithImage
              onCancel={() => handleCloseDeleteModal()}
              open={isOpenDeleteModal}
              title='Supprimer le document'
              subtitle='Êtes-vous sûr de vouloir supprimer ce document ?'
              image={{
                src: "https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png",
                srcSet: "https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png?scale-down-to=512 512w,https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png?scale-down-to=1024 1024w,https://framerusercontent.com/images/DOPh6i0F3gfgI3TzaQYkACOPw.png 1574w",
                sizes: "165.6842px",
                alt: "",
              }}
              actions={[
                {
                  label: 'Annuler',
                  onClick: () => handleCloseDeleteModal(),
                  type: 'deleteButton'
                },
                {
                  label: 'Confirmer',
                  onClick: () => handleDeleteContract(selectedDocument?.id),
                  type: 'validateButton'
                }
              ]}
            />
          )
        }


      </div >
    </>
  );
};


const styles = ({ spacing }) => ({
  "framerP": {
    fontFamily: "\"Montserrat\", sans-serif",
    fontSize: "20px",
    fontWeight: "600",
    lineHeight: "15px",
    color: "#152652",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",

    "&::placeholder": {
      color: "#5D75A8",
    },
  },
  "framerDiv": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "10px",
  },
  "framerDiv2": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    height: "min-content",
    justifyContent: "space-between",
    width: "100%",
    overflow: "hidden",
  },
  "framerDiv3": {
    alignContent: "center",
    display: "flex",
    flexDirection: "row",
    height: "min-content",
    gap: "4px",
    overflow: "hidden",
    placeItems: "center",
  },
  "framerDiv4": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    height: "min-content",
    justifyContent: "flex-start",
    gap: "10px",
    overflow: "hidden",
  },
  "actionButton": {
    transition: "none",
    border: "1px solid #c0d9ff",
    alignContent: "center",
    backgroundColor: "#f3f7ff",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "row",
    height: "40px",
    width: "40px",
    placeItems: "center",
    padding: "0",
    "&:hover": {
      borderColor: "#0060f9 !important",
      "svg": {
        fill: "#0060f9 !important",
      },
    },
  },
  "framerButton2": {
    transition: "none !important",
    borderBottomWidth: "1px",
    borderColor: "#c0d9ff",
    borderLeftWidth: "1px",
    borderRightWidth: "1px",
    borderStyle: "solid",
    borderTopWidth: "1px",
    alignContent: "center",
    backgroundColor: "#f3f7ff",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    display: "flex",
    flexDirection: "row",
    height: "30px",
    width: "30px",
    placeItems: "center",
    "&:hover": {
      borderColor: "#0060f9 !important",
      "svg": {
        fill: "#0060f9 !important",
      },
    },
  },
  "colorButton": {
    padding: "4px"
  },
  "framerIcon96": {
    height: "16px",
    width: "16px",
    fill: "#5d75a8",
  },
  "actionIcon": {
    height: "16px",
    width: "16px",
    fill: "#5d75a8",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default ConventionHeader;